import React, { useCallback, useRef, useState } from 'react';
import styles from './_styles.module.scss';
import { useAppDispatch, useAppSelector } from '../../../core/hooks/reduxHooks';
import { modalDialogSlice } from '../../../redux/reducers/ui/ModalDialogSlice';
import UITextarea from '../../UI/UITextarea/UITextarea';
import UIButton from '../../UI/UIButton/UIButton';
import Icon from '../../UI/UIIcon/UIIcon';
import { StarIcon, HollowStarIcon } from '../../UI/StarIcon/StarIcon';
import { emojisRegex } from '../../../core/constants/regex';
import { employerAPI } from '../../../core/api/api';
import { setProposalRating } from '../../../redux/reducers/api/UserRatingsSlice';

const validateRatingComment = (value: string): boolean => {
  return (
    value.match(emojisRegex) === null &&
    !value.includes('!') &&
    !value.includes('?') &&
    !value.includes('#')
  );
};

const StarRating = ({
  rating,
  onRatingChange,
  isError,
}: {
  rating: number;
  onRatingChange: (rating: number) => void;
  isError: boolean;
}) => {
  const [hoverRating, setHoverRating] = useState<number>(0);

  return (
    <div className={styles.star_rating} data-error={isError}>
      <span className={styles.rating_title}>Rating:</span>
      {[...Array(5)].map((_, index) => (
        <span
          key={index}
          onClick={() => onRatingChange(index + 1)}
          onMouseEnter={() => setHoverRating(index + 1)}
          onMouseLeave={() => setHoverRating(0)}
        >
          {index + 1 <= rating || index + 1 <= hoverRating ? (
            <StarIcon />
          ) : (
            <HollowStarIcon />
          )}
        </span>
      ))}
    </div>
  );
};

const RateCandidateModal: React.FC = () => {
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [rating, setRating] = useState<number>(0);
  const [isRatingError, setIsRatingError] = useState<boolean>(false);
  const [commentError, setCommentError] = useState<string>('');
  const applyFormRef = useRef(null);
  const dispatch = useAppDispatch();
  const { posterInfo } = useAppSelector(state => state.postUserJobReducer);
  const { rateProposalId } = useAppSelector(state => state.modalDialogReducer);
  const { closeModal } = modalDialogSlice.actions;
  const onSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();

      let error = false;

      if (rating === 0) {
        setIsRatingError(true);
        error = true;
      }

      if (comment.length > 0 && !validateRatingComment(comment)) {
        setCommentError('Please do not enter emoji, !, ? and #.');
        error = true;
      }

      if (error) {
        return;
      }

      employerAPI
        .rateCandidate({ rating, comment, proposal_id: rateProposalId })
        .then(() => {
          setIsSuccess(true);
          dispatch(
            setProposalRating({
              proposalId: rateProposalId,
              rating: { rating, comment },
            })
          );
        });
    },
    [rating, comment, rateProposalId]
  );
  const onCloseModal = () => {
    dispatch(closeModal());
  };

  const handleRatingChange = (ratingNum: number) => {
    setRating(ratingNum);
    setIsRatingError(false);
  };

  if (isSuccess) {
    return (
      <div className={styles.modal}>
        <div className={styles.modal_head}>
          <h3 className={styles.heading}>Rate applicant</h3>
          <button
            className={styles.close}
            type='button'
            onClick={() => onCloseModal()}
          >
            <Icon name={'close'} className={styles.close_icon} />
          </button>
        </div>
        <div className={styles.success_message}>
          <p>Thanks for rating the applicant.</p>
          <p>
            Your rating will help employers to make informed decisions before
            hiring.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modal_head}>
        <h3 className={styles.heading}>Rate applicant</h3>
        <button
          className={styles.close}
          type='button'
          onClick={() => onCloseModal()}
        >
          <Icon name={'close'} className={styles.close_icon} />
        </button>
      </div>
      <form
        ref={applyFormRef}
        onSubmit={onSubmit}
        className={styles.rating_form}
      >
        <StarRating
          rating={rating}
          onRatingChange={handleRatingChange}
          isError={isRatingError}
        />
        <label className={styles.description}>
          <UITextarea
            id={'comment'}
            title={'Comments:'}
            rows={5}
            fontSizeInput={14}
            maxLength={posterInfo.rating_comment_character_limit}
            value={comment}
            placeholder='Please key in your comments...'
            onChange={event => {
              setCommentError('');
              setComment(event.target.value);
            }}
            color={'employer'}
            error={commentError !== ''}
          />
        </label>
        <span className={styles.comment_error}>{commentError}</span>
        <div className={styles.buttons}>
          <UIButton
            title={'Submit'}
            type={'submit'}
            classModificator={styles.submit_btn}
          />
        </div>
      </form>
    </div>
  );
};

export default RateCandidateModal;
